import React, { ReactNode } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { PermissionLevel } from '../types';
import { RouteSkeleton } from '../../../Components/Skeleton/RouteSkeleton';
import './ProtectedRoute.css';

import { createLogger } from '../../../utils/logger';
const logger = createLogger('features-auth');

interface ProtectedRouteProps {
  // Legacy: Sistema de roles (mantener para backward compatibility)
  requiredRoles?: string[];

  // Nuevo sistema dinámico: Nombre del recurso (ej: "Home", "Settings", "Assistants")
  // El nombre será resuelto dinámicamente a UUID desde la base de datos
  requiredResource?: string;

  // Alias para requiredResource (para mantener compatibilidad)
  requiredPermission?: string;

  // Nivel de permiso requerido (VIEW o EDIT)
  requiredLevel?: PermissionLevel;

  children?: ReactNode;
}

export function ProtectedRoute({
  requiredRoles = [],
  requiredResource,
  requiredPermission,
  requiredLevel = PermissionLevel.VIEW,
  children
}: ProtectedRouteProps) {
  const { isAuthenticated, user, loading, hasPermission, permissions } = useAuth();

  // Use requiredPermission as alias for requiredResource
  const resource = requiredPermission || requiredResource;

  // Show loading while authenticating OR while permissions are being loaded
  if (loading) {
    return <RouteSkeleton />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // If using permission-based validation, wait for permissions to load
  const needsPermissions = resource;
  const permissionsLoading = needsPermissions && (!permissions || permissions.length === 0);

  if (permissionsLoading) {
    return <RouteSkeleton />;
  }

  // MODO 1: Sistema de roles (LEGACY - mantener para backward compatibility)
  if (requiredRoles.length > 0) {
    if (!user?.role || !requiredRoles.includes(user.role)) {
      return <Navigate to="/unauthorized" />;
    }

    return children || <Outlet />;
  }

  // MODO 2: Sistema dinámico - requiredResource/requiredPermission (nombre del recurso)
  // El nombre será resuelto dinámicamente a UUID usando el cache de resourceService
  if (resource) {
    const hasAccess = hasPermission(resource, requiredLevel);

    if (!hasAccess) {
      logger.warn(`⚠️ Access denied to resource: "${resource}" (level: ${requiredLevel})`);
      return <Navigate to="/unauthorized" />;
    }

    return children || <Outlet />;
  }

  // CASO DEFAULT: Sin restricciones (solo requiere autenticación)
  return children || <Outlet />;
} 